import React, { useCallback, useEffect, useState, FC } from 'react';
import { documentService } from '../../../../utils/window.utils';
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';
import ctaTypes from '../../../../enums/CTA.enums';
import DictionaryTerm from '../../../@shared/dictionaryTerm';
import { TDealerFilterComponentProps, TSelectedFilterOptions } from '../../types/dealerLocatorLanding.types';
import './styles.scss';
import Accordion from './components/accordion';

const MobileFilter: FC<TDealerFilterComponentProps> = ({ filters, selectedFilters, updateFilters }) => {
  const [shouldOpenFilterModal, setShouldOpenFilterModal] = useState(false);
  // Todo: Replace this code with line no. 16 for Services Ref US-446445
  //  const { Equipments, Services } = filters;
  // End Code comments US-446445
  const { Equipments } = filters;
  const { equipmentSelectedOptions, serviceSelectedOptions } = selectedFilters;
  const [localSelectedEquipment, setLocalSelectedEquipment] = useState<TSelectedFilterOptions>(equipmentSelectedOptions);
  const [localSelectedServices, setLocalSelectedServices] = useState<TSelectedFilterOptions>(serviceSelectedOptions);
  const [selectedAccordion, setSelectedAccordion] = useState<'Equipment' | 'Services' | undefined>();
  const isNoCheckboxSelected = Object.keys(localSelectedEquipment).length + Object.keys(localSelectedServices).length === 0;
  const [canShowCTAs, setCanShowCTAs] = useState(false);

  const toggleModal = (shouldOpenModal: boolean) => {
    if (!shouldOpenModal) setSelectedAccordion(undefined);
    shouldOpenModal && setCanShowCTAs(!isNoCheckboxSelected); // show CTA's on modal open if any checkbox selected
    setShouldOpenFilterModal(shouldOpenModal);
  };

  const getFiltersSelectedCount = (): string => {
    const count = Object.values({ ...equipmentSelectedOptions, ...serviceSelectedOptions })?.length || 0;
    if (count) {
      return ` (${count})`;
    } else {
      return '';
    }
  };

  const revertToOldFilters = () => {
    setLocalSelectedEquipment({ ...equipmentSelectedOptions });
    setLocalSelectedServices({ ...serviceSelectedOptions });
  };

  const resetFilter = () => {
    if (isNoCheckboxSelected) return;
    updateFilters({
      equipmentSelectedOptions: {},
      serviceSelectedOptions: {},
    });
  };

  const applyFilter = () => {
    updateFilters({
      equipmentSelectedOptions: { ...localSelectedEquipment },
      serviceSelectedOptions: { ...localSelectedServices },
    });
    toggleModal(false);
  };

  const handleCheckBoxClick = () => {
    !canShowCTAs && setCanShowCTAs(true);
  };

  // 1. update localselected values wiht real selected values on filters reset & if user shifts from dsk to < tablet layout
  useEffect(() => {
    setLocalSelectedEquipment({ ...equipmentSelectedOptions });
    setLocalSelectedServices({ ...serviceSelectedOptions });
  }, [equipmentSelectedOptions, serviceSelectedOptions]);

  useEffect(() => {
    const docElement = documentService();
    if (!docElement?.body) return;

    if (shouldOpenFilterModal) docElement.body.classList.add('body--with-dealer-filter-model');
    else docElement.body.classList.remove('body--with-dealer-filter-model');
  }, [shouldOpenFilterModal]);

  const toggleAccordion = (accordionName: typeof selectedAccordion) => {
    if (accordionName === selectedAccordion) setSelectedAccordion(undefined);
    else {
      setSelectedAccordion(accordionName);
    }
  };

  const getTotalFiltersSelected = useCallback(
    (filtersObj = {}): string => {
      const count = Object.values(filtersObj)?.length || 0;
      if (count) {
        return ` (${count})`;
      } else {
        return '';
      }
    },
    [filters]
  );

  const equipmentSelectedFiltersCount = getTotalFiltersSelected(localSelectedEquipment);
  // Todo: Uncomment this code for Services against US-446445
  // const seriesSelectedFiltersCount = getTotalFiltersSelected(localSelectedServices);
  // End Code comments US-446445
  return (
    <>
      <div className="dealer-locator-mbl-filters">
        <div
          className="dealer-locator-mbl-modal-switch"
          onClick={() => {
            toggleModal(true);
          }}
        >
          <DictionaryTerm keyword="cnhi-dealerFilterTitle" /> {getFiltersSelectedCount()}
          <Icon iconName="set-nav-services-funnel" size={iconSize['dsk']} />
        </div>
        {shouldOpenFilterModal && (
          <div className="dealer-filter-modal">
            <div className="dealer-filter-modal__header">
              <span className="dealer-filter-modal__title">
                <DictionaryTerm keyword="cnhi-dealerFilterTitle" />
              </span>
              <Icon
                iconName="action-close"
                color="white"
                size={iconSize['dsk']}
                onClick={() => {
                  revertToOldFilters();
                  toggleModal(false);
                }}
              />
            </div>
            <div className="dealer-filter-modal__body">
              <Accordion
                accordionOptions={Equipments}
                accordionTitle={
                  <>
                    <DictionaryTerm keyword="cnhi-dealerLocatorEquipment" />
                    {equipmentSelectedFiltersCount}
                  </>
                }
                localSelectedFilter={localSelectedEquipment}
                setLocalSelectedFilter={setLocalSelectedEquipment}
                canShowAccordionContent={selectedAccordion === 'Equipment'}
                handleSwitchClick={() => toggleAccordion('Equipment')}
                handleCheckBoxClick={handleCheckBoxClick}
              />
              {/* Todo: Uncomment this code for Services against US-446445
              <Accordion
                accordionOptions={Services}
                accordionTitle={
                  <>
                    <DictionaryTerm keyword="cnhi-dealerLocatorService" />
                    {seriesSelectedFiltersCount}
                  </>
                }
                localSelectedFilter={localSelectedServices}
                setLocalSelectedFilter={setLocalSelectedServices}
                canShowAccordionContent={selectedAccordion === 'Services'}
                handleSwitchClick={() => toggleAccordion('Services')}
                handleCheckBoxClick={handleCheckBoxClick}
              />
              End Code comments US-446445 */}
            </div>
            {canShowCTAs && (
              <div className="dealer-filter-modal__ctas">
                <span
                  className={`${ctaTypes['baseButton']} ${ctaTypes['primaryWhite']} ${
                    isNoCheckboxSelected ? 'dealer-filter-modal__cta--disabled' : ''
                  }`}
                  onClick={resetFilter}
                >
                  <DictionaryTerm keyword="cnhi-dealerLocatorResetFilter" />
                </span>
                <span className={`${ctaTypes['baseButton']} ${ctaTypes['primaryRed']}`} onClick={applyFilter}>
                  <DictionaryTerm keyword="cnhi-dealerLocatorApplyFilter" />
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default MobileFilter;
